// AWIN client-side tracking service

export const loadAwinMasterTag = () => {
  if (typeof document === 'undefined') {
    // console.debug('AWIN: Document not available (SSR)')
    return
  }

  if (!process.env.GATSBY_AWIN_ADVERTISER_ID) {
    console.warn('AWIN: Advertiser ID not configured')
    return
  }

  const advertiserId = process.env.GATSBY_AWIN_ADVERTISER_ID
  const existingScript = document.querySelector(
    `script[src*="dwin1.com/${advertiserId}.js"]`
  )

  if (existingScript) {
    console.debug('AWIN: MasterTag already loaded')
    return
  }

  try {
    const awMastertag = document.createElement('script')
    awMastertag.setAttribute('defer', 'defer')
    awMastertag.src = `https://www.dwin1.com/${advertiserId}.js`
    awMastertag.type = 'text/javascript'

    awMastertag.onload = () => {
      console.debug('AWIN: MasterTag loaded successfully')
    }

    awMastertag.onerror = () => {
      console.error('AWIN: Failed to load MasterTag')
    }

    document.getElementsByTagName('body')[0].appendChild(awMastertag)
    console.debug('AWIN: MasterTag script added to DOM')
  } catch (error) {
    console.error('AWIN: Error loading MasterTag:', error)
  }
}

// Commissionable amount for an order: total minus shipping and tax.
export const getAwinCommissionableAmount = (cart) => {
  const total = parseFloat(cart?.total) || 0
  const shipTotal = parseFloat(cart?.shipTotal) || 0
  const taxTotal = parseFloat(cart?.taxTotal) || 0

  return Math.max(total - shipTotal - taxTotal, 0).toFixed(2)
}

// Track conversion using AWIN's conversion tracking
export const trackAwinConversion = (orderData) => {
  if (typeof window === 'undefined') {
    console.debug('AWIN: Window not available (SSR)')
    return
  }

  if (!orderData) {
    console.debug('AWIN: Conversion tracking skipped (no order data)')
    return
  }

  if (!orderData.totalAmount || !orderData.orderReference) {
    console.error(
      'AWIN: Missing required order data for conversion tracking',
      orderData
    )
    return
  }

  try {
    // Set up AWIN conversion tracking object
    window.AWIN = window.AWIN || {}
    window.AWIN.Tracking = window.AWIN.Tracking || {}
    window.AWIN.Tracking.Sale = {}

    // Map order data to AWIN format
    const amount = parseFloat(orderData.totalAmount).toFixed(2)
    const isTestMode = process.env.GATSBY_AWIN_TEST_MODE === '1' ? '1' : '0'

    window.AWIN.Tracking.Sale.amount = amount
    window.AWIN.Tracking.Sale.channel = 'aw'
    window.AWIN.Tracking.Sale.orderRef = orderData.orderReference
    window.AWIN.Tracking.Sale.parts = `DEFAULT:${amount}`
    window.AWIN.Tracking.Sale.currency = orderData.currency || 'USD'
    window.AWIN.Tracking.Sale.voucher = orderData.voucherCode || ''
    window.AWIN.Tracking.Sale.test = isTestMode

    console.debug(
      'AWIN: Conversion tracking data set:',
      window.AWIN.Tracking.Sale
    )

    // Trigger conversion if the MasterTag has loaded. Otherwise, the MasterTag
    // will pick up AWIN.Tracking.Sale and fire the conversion once it loads.
    if (typeof window.AWIN.Tracking.run === 'function') {
      window.AWIN.Tracking.run()
      console.debug('AWIN: Conversion tracking fired via JavaScript')
    } else {
      console.debug(
        'AWIN: MasterTag not loaded yet, conversion will fire when it loads'
      )
    }

    // Also trigger client-side fallback conversion pixel
    triggerAwinFallbackPixel(orderData)
  } catch (error) {
    console.error('AWIN: Error in conversion tracking:', error, orderData)
    // Still try fallback pixel even if JS tracking fails
    triggerAwinFallbackPixel(orderData)
  }
}

// Triggers client-side AWIN fallback pixel by adding img tag to page.
export const triggerAwinFallbackPixel = (orderData) => {
  if (typeof document === 'undefined') {
    // console.debug('AWIN: Document not available (SSR)')
    return
  }

  if (!orderData || !orderData.totalAmount || !orderData.orderReference) {
    console.debug('AWIN: Fallback pixel skipped (missing data)')
    return
  }

  if (!process.env.GATSBY_AWIN_ADVERTISER_ID) {
    console.warn('AWIN: Advertiser ID not configured for fallback pixel')
    return
  }

  try {
    // Build AWIN fallback pixel URL
    const pixelUrl = new URL('https://www.awin1.com/sread.img')
    const amount = parseFloat(orderData.totalAmount).toFixed(2)
    const isTestMode = process.env.GATSBY_AWIN_TEST_MODE === '1' ? '1' : '0'

    pixelUrl.searchParams.set('tt', 'ns') // Tracking type
    pixelUrl.searchParams.set('tv', '2') // Tracking version
    pixelUrl.searchParams.set('merchant', process.env.GATSBY_AWIN_ADVERTISER_ID)
    pixelUrl.searchParams.set('amount', amount)
    pixelUrl.searchParams.set('ch', 'aw') // Channel
    pixelUrl.searchParams.set('parts', `DEFAULT:${amount}`) // Commission group
    pixelUrl.searchParams.set('ref', orderData.orderReference)
    pixelUrl.searchParams.set('cr', orderData.currency || 'USD')
    pixelUrl.searchParams.set('vc', orderData.voucherCode || '')
    pixelUrl.searchParams.set('testmode', isTestMode)

    // Create and append invisible img tag
    const img = document.createElement('img')
    img.src = pixelUrl.toString()
    img.style.display = 'none'
    img.width = 1
    img.height = 1
    img.onload = () => {
      console.debug('AWIN: Fallback pixel fired successfully')
    }
    img.onerror = () => {
      console.error('AWIN: Fallback pixel failed to load')
    }

    document.body.appendChild(img)

    console.debug('AWIN: Fallback pixel img tag added to page')
  } catch (error) {
    console.error('AWIN: Error creating fallback pixel:', error)
  }
}
